import './shiki-highlight.css';
import './mesa-diff.css';
import { diffWords } from 'diff';
import { SeparatorHorizontalIcon, Loader2Icon } from 'lucide-react';
import React, {
  useCallback,
  useRef,
  MouseEvent,
  JSX,
  useMemo,
  createContext,
  useContext,
  memo,
  useState,
  useEffect,
} from 'react';

import { FloatingCommentMenu, CommentSelectionRange } from './floating-comment-menu';
import { LineTokenRenderer } from './token-renderer';

import { useReviewComments, type ReviewComment } from '@/features/comments/hooks/use-review-comments';
import { useCommentNavigation, useSidebar } from '@/features/pulls/context/sidebar-context';
import { useRawFileContent } from '@/hooks/use-raw-file-content';
import { type DiffHunk, type DiffChange, type ProcessedHunk } from '@/lib/diff-parser';
import { highlightAsyncTokens, highlightHunkTokens, type Token } from '@/lib/highlight-client';
import {
  extractLinesFromRaw,
  createContextLines,
  calculateGapRange,
  createGapId,
  validateGapExpansion,
} from '@/lib/line-extraction';
import { cn, getSelectedTextNodes } from '@/lib/utils';

const BASE_CONTENT_CLASSES = 'diff-code px-0 break-all';
const INSERT_CONTENT_CLASSES = cn('bg-green-50 dark:bg-green-950', BASE_CONTENT_CLASSES);
const DELETE_CONTENT_CLASSES = cn('bg-red-50/70 dark:bg-red-950', BASE_CONTENT_CLASSES);
const OMIT_CONTENT_CLASSES = cn('bg-stone-100 dark:bg-stone-900', BASE_CONTENT_CLASSES);
const GUTTER_CLASSES = 'diff-gutter px-2 text-right text-muted-foreground w-[48px]';

const WORD_DELETE_CLASSES = 'bg-red-200/50 dark:bg-red-900';
const WORD_INSERT_CLASSES = 'bg-green-200 dark:bg-green-900';

// --- Shared UI helpers ---
const AVATAR_CELL_CLASSES = 'w-6 px-0 overflow-visible relative border-r';
const AVATAR_IMG_CLASSES =
  'absolute left-1/2 -translate-x-[50%] top-1/2 -translate-y-[50%] w-5 h-5 rounded-full border-2 border-amber-100 shadow-md cursor-pointer hover:opacity-80 transition-opacity z-10';
const COMMENT_COUNT_CLASSES =
  'absolute left-1/2 -translate-x-[50%] top-1/2 -translate-y-[50%] w-5 h-5 rounded-full bg-yellow-600 text-white text-[10px] flex items-center justify-center font-semibold shadow-md cursor-pointer hover:opacity-80 transition-opacity z-10';

type FileContextType = {
  filePath: string;
};

const FileContext = createContext<FileContextType | undefined>(undefined);

function useFileContext() {
  const ctx = useContext(FileContext);
  if (!ctx) throw new Error('useFileContext must be used within FileContext.Provider');
  return ctx;
}

// Context for diff event handlers
type DiffEventContextType = {
  onContentMouseUp: () => void;
};

const DiffEventContext = createContext<DiffEventContextType | undefined>(undefined);

function useDiffEvents() {
  const ctx = useContext(DiffEventContext);
  if (!ctx) throw new Error('useDiffEvents must be used within DiffEventContext.Provider');
  return ctx;
}

const AvatarCell = memo(
  function ({
    comment,
    showAvatar,
    commentCount = 1,
    lineNumber,
    side,
  }: {
    comment?: ReviewComment | null;
    showAvatar: boolean;
    commentCount: number;
    lineNumber: number;
    side: DiffSide;
  }) {
    // Try to use the comment navigation helpers from context, but gracefully handle if unavailable
    const { navigateToComment, navigateToLocation } = useCommentNavigation();
    const { filePath } = useFileContext();

    // Handle clicking on the avatar or comment count
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent bubbling

        // Find the actual table row that contains this avatar
        const row = e.currentTarget.closest('tr.diff-line');
        if (!row) return;

        // Only proceed if we have a comment and it's not outdated
        if (!comment || comment.isOutdated) {
          return;
        }

        // Store the row ID so we can find it again later
        const rowId = `avatar-clicked-${Date.now()}`;
        row.setAttribute('data-avatar-clicked', rowId);

        // Use context navigation if available
        if (commentCount === 1) {
          navigateToComment(comment.gid);
        } else {
          navigateToLocation(filePath, lineNumber, side);
        }

        // After the sidebar opens and layout changes, scroll back to this row
        requestAnimationFrame(() => {
          const scrollToMarkedRow = () => {
            try {
              const clickedRow = document.querySelector(`[data-avatar-clicked="${rowId}"]`);
              if (!clickedRow) return;

              // Check if row is already visible in the viewport
              const rect = clickedRow.getBoundingClientRect();
              const isVisible =
                rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

              // Only scroll if not already fully visible
              if (!isVisible) {
                clickedRow.scrollIntoView({
                  behavior: 'auto',
                  block: 'center',
                });
              }

              // Remove the temporary attribute
              clickedRow.removeAttribute('data-avatar-clicked');
            } catch (err) {
              console.error('Error scrolling to clicked row:', err);
            }
          };

          // Try immediately and then again after a delay to account for sidebar animation
          scrollToMarkedRow();
          setTimeout(scrollToMarkedRow, 200);
        });
      },
      [comment, commentCount, lineNumber, filePath, side, navigateToComment, navigateToLocation]
    );

    // Only render the img if needed, otherwise return an empty cell
    // This avoids recreating the img element on every render
    const avatarContent = useMemo(() => {
      if (!showAvatar) return null;

      const isOutdated = comment?.isOutdated;
      const isResolved = comment?.isResolved;
      const opacityClass = (isOutdated || isResolved) ? 'opacity-60' : '';

      if (commentCount > 1) {
        return (
          <div
            className={cn(COMMENT_COUNT_CLASSES, opacityClass)}
            title={`${commentCount} comments${isOutdated ? ' (outdated)' : ''}${isResolved ? ' (resolved)' : ''}`}
            onClick={handleClick}
          >
            {commentCount}
          </div>
        );
      }

      if (comment) {
        return (
          <img
            src={comment.author.avatarUrl}
            alt={comment.author.name}
            title={`${comment.author.name}${isOutdated ? ' (outdated)' : ''}${isResolved ? ' (resolved)' : ''}`}
            className={cn(AVATAR_IMG_CLASSES, opacityClass)}
            onClick={handleClick}
          />
        );
      }

      return null;
    }, [comment?.gid, comment?.isOutdated, comment?.isResolved, showAvatar, commentCount, handleClick]);

    return <td className={cn(AVATAR_CELL_CLASSES)}>{avatarContent}</td>;
  },
  (prevProps, nextProps) => {
    // Only re-render if showAvatar changed, comment ID changed, or count changed
    if (prevProps.showAvatar !== nextProps.showAvatar) return false;
    if (prevProps.commentCount !== nextProps.commentCount) return false;
    if (!prevProps.comment && !nextProps.comment) return true;
    if (!prevProps.comment || !nextProps.comment) return false;
    return prevProps.comment.gid === nextProps.comment.gid && 
           prevProps.comment.isOutdated === nextProps.comment.isOutdated &&
           prevProps.comment.isResolved === nextProps.comment.isResolved;
  }
);

// Precompute number cells for common line numbers
const numberCellCache = new Map<number | string | undefined, JSX.Element>();

const NumberCell = memo(
  function ({ number }: { number: number | string | undefined }) {
    // Get from cache or create new cell
    if (number !== undefined && typeof number === 'number' && !numberCellCache.has(number)) {
      numberCellCache.set(number, <td className={GUTTER_CLASSES}>{number}</td>);
    }

    return numberCellCache.get(number) || <td className={GUTTER_CLASSES}>{number}</td>;
  },
  (prevProps, nextProps) => prevProps.number === nextProps.number
);

export type ViewMode = 'unified' | 'split';

// State for tracking gap expansion
type GapState = 'collapsed' | 'expanded' | 'loading' | 'error';

export interface MesaDiffProps {
  /**
   * The hunks to render in the diff view
   */
  hunks: DiffHunk[];

  /**
   * The view mode to use for rendering the diff
   * @default 'unified'
   */
  viewMode?: ViewMode;

  /**
   * Optional className to apply to the root element
   */
  className?: string;

  /**
   * The language to use for syntax highlighting
   * If not provided, will try to detect from file extension
   */
  language?: string;

  /**
   * The filename to detect language from if language is not provided
   */
  filename: string;

  /**
   * Repository owner (required for code folding)
   */
  owner?: string;

  /**
   * Repository name (required for code folding)
   */
  repo?: string;

  /**
   * Commit SHA (required for code folding)
   */
  sha?: string;
}

function findClosest(target: HTMLElement, className: string) {
  let current: HTMLElement | null = target;
  while (current && current !== document.documentElement && !current.classList.contains(className)) {
    current = current.parentElement;
  }
  return current === document.documentElement ? null : current;
}

function setUserSelectStyle(element: Element, selectable: boolean) {
  const value = selectable ? 'text' : 'none';
  if (element instanceof HTMLElement) {
    element.style.userSelect = value;
    // NOTE: this is marked as deprecated in the spec, but it's still needed for Safari
    element.style.webkitUserSelect = value;
  }
}

// Token diff helper function
function getTokenDiff(oldStr: string, newStr: string) {
  // Returns array of { value, added?, removed? }
  return diffWords(oldStr, newStr);
}

type DiffBlock =
  | { type: 'normal'; change: DiffChange }
  | { type: 'replace'; deletes: DiffChange[]; inserts: DiffChange[] }
  | { type: 'delete'; deletes: DiffChange[] }
  | { type: 'insert'; inserts: DiffChange[] };

function getNextBlock(changes: DiffChange[], start: number): [DiffBlock, number] {
  const change = changes[start];
  if (change.type === 'normal') {
    return [{ type: 'normal', change }, start + 1];
  }
  if (change.type === 'delete') {
    const deletes: DiffChange[] = [];
    let i = start;
    while (i < changes.length && changes[i].type === 'delete') deletes.push(changes[i++]);
    const inserts: DiffChange[] = [];
    let j = i;
    while (j < changes.length && changes[j].type === 'insert') inserts.push(changes[j++]);
    if (inserts.length > 0) {
      return [{ type: 'replace', deletes, inserts }, j];
    }
    return [{ type: 'delete', deletes }, i];
  }
  if (change.type === 'insert') {
    const inserts: DiffChange[] = [];
    let i = start;
    while (i < changes.length && changes[i].type === 'insert') inserts.push(changes[i++]);
    return [{ type: 'insert', inserts }, i];
  }
  // fallback
  return [{ type: 'normal', change }, start + 1];
}

function* diffBlockIterator(changes: DiffChange[]): Generator<DiffBlock> {
  let i = 0;
  while (i < changes.length) {
    const [block, next] = getNextBlock(changes, i);
    yield block;
    i = next;
  }
}

// Helper function to detect language from file extension
function detectLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'js':
      return 'javascript';
    case 'jsx':
      return 'jsx';
    case 'ts':
      return 'typescript';
    case 'tsx':
      return 'tsx';
    case 'css':
      return 'css';
    case 'html':
    case 'htm':
      return 'markup';
    case 'lock':
    case 'json':
      return 'json';
    case 'ex':
    case 'exs':
      return 'elixir';
    default:
      return ext ?? 'markup';
  }
}

// --- DiffSyntaxContext ---
interface DiffSyntaxContextValue {
  tokenCache: Map<string, Token[]>;
  setTokenCache: React.Dispatch<React.SetStateAction<Map<string, Token[]>>>;
  language: string;
}

const DiffSyntaxContext = createContext<DiffSyntaxContextValue | undefined>(undefined);

function useDiffSyntax() {
  const ctx = useContext(DiffSyntaxContext);
  if (!ctx) throw new Error('useDiffSyntax must be used within DiffSyntaxContext.Provider');
  return ctx;
}

// Simple cached highlight component using tokens
const LineHighlight = memo(
  function ({ code }: { code: string }) {
    const { tokenCache, setTokenCache, language } = useDiffSyntax();
    const cached = tokenCache.get(code);
    const [tokens, setTokens] = useState<Token[] | null>(cached ?? null);

    useEffect(() => {
      if (tokens) return;

      highlightAsyncTokens(code, language)
        .then((t) => {
          if (t) {
            setTokenCache((prev) => new Map(prev).set(code, t));
            setTokens(t);
          }
        })
        .catch(() => {
          // Silently handle errors
        });
    }, [code, tokens, setTokenCache, language]);

    return <LineTokenRenderer tokens={tokens} fallback={code.length === 0 ? '\n' : code} />;
  },
  (prevProps, nextProps) => prevProps.code === nextProps.code
);

// Add a cache for highlighted content
const highlightCache = new Map<string, React.ReactNode>();

function highlight(
  code: string,
  diffTokens?: ReturnType<typeof getTokenDiff>,
  tokenType?: 'insert' | 'delete'
): React.ReactNode {
  if (code.length === 0) return '\n';

  // Create a cache key
  const cacheKey = `${code}|${tokenType || ''}|${!!diffTokens}`;

  // Check if result is already cached
  if (highlightCache.has(cacheKey)) {
    return highlightCache.get(cacheKey);
  }

  let result;
  if (!diffTokens) {
    result = <LineHighlight code={code} />;
  } else {
    result = diffTokens.map((token, i) => {
      const rendered = <LineHighlight code={token.value} />;
      if (token.added && tokenType === 'insert') {
        return (
          <span key={i} className={WORD_INSERT_CLASSES}>
            {rendered}
          </span>
        );
      }
      if (token.removed && tokenType === 'delete') {
        return (
          <span key={i} className={WORD_DELETE_CLASSES}>
            {rendered}
          </span>
        );
      }
      if (!token.added && !token.removed) {
        return <span key={i}>{rendered}</span>;
      }
      return null;
    });
  }

  // Store in cache and return
  highlightCache.set(cacheKey, result);
  return result;
}

const DiffSyntaxProvider = memo(function ({ language, children }: { language: string; children: React.ReactNode }) {
  const [tokenCache, setTokenCache] = useState<Map<string, Token[]>>(new Map());

  const contextValue = useMemo(() => ({ tokenCache, setTokenCache, language }), [tokenCache, setTokenCache, language]);

  return <DiffSyntaxContext.Provider value={contextValue}>{children}</DiffSyntaxContext.Provider>;
});

// Simpler prefetch component
const HunkPrefetch = memo(
  function ({
    hunk,
    language,
    hunkKey,
    children,
  }: {
    hunk: DiffHunk;
    language: string;
    hunkKey: string;
    children: React.ReactNode;
  }) {
    // Use a ref to track if we've already prefetched this hunk
    const prefetchedRef = useRef(false);

    useEffect(() => {
      // Only prefetch once per hunk instance
      if (prefetchedRef.current) return;
      prefetchedRef.current = true;

      const code = hunk.changes.map((c) => c.content).join('\n');

      // Use a small delay to prioritize visible content rendering first
      const timeoutId = setTimeout(() => {
        highlightHunkTokens(hunkKey, language, code).catch(() => {});
      }, 50);

      return () => {
        clearTimeout(timeoutId);
      };
    }, [hunkKey, language]);

    return <>{children}</>;
  },
  (prevProps, nextProps) => {
    // Only re-render if the key changes
    return prevProps.hunkKey === nextProps.hunkKey;
  }
);

type DiffSide = 'LEFT' | 'RIGHT';

function isLineInCommentRange(lineNumber: number | undefined, side: DiffSide | undefined, comment: ReviewComment) {
  if (lineNumber === undefined) return false;

  // First check if we're in the line range
  const inLineRange = lineNumber >= comment.startLine && lineNumber <= comment.endLine;
  if (!inLineRange) return false;

  // If we're looking for a specific side
  if (side) {
    return side === comment.startSide || side === comment.endSide;
  }

  // If no side specified, it's in range if the line numbers match
  return true;
}

/**
 * Determines whether to show the avatar/count for a comment on a given line.
 * Rules:
 * - For multi-line comments (startLine !== endLine): show on startLine only
 * - For single-line comments (startLine === endLine): show on the line (startLine/endLine)
 */
function shouldShowAvatarForComment(comment: ReviewComment, currentLine: number): boolean {
  if (comment.startLine === comment.endLine) {
    // Single-line comment: show on the comment line
    return currentLine === comment.startLine;
  } else {
    // Multi-line comment: show only on the start line
    return currentLine === comment.startLine;
  }
}

/**
 * Finds comments that should display their avatar/count on a specific line.
 * This is different from findCommentsForLine which finds all comments that touch a line.
 * This function only returns comments that should actually show their avatar on this line.
 */
function findCommentsForGutterDisplay(
  lineNumber: number | undefined,
  side: DiffSide | undefined,
  filePath: string,
  comments: ReviewComment[],
  areResolvedCommentsCollapsed?: boolean
): ReviewComment[] {
  if (lineNumber === undefined) return [];

  return comments.filter((comment) => {
    // Must be same file
    if (filePath !== comment.path) return false;

    // Must be a comment that should show its avatar on this line
    if (!shouldShowAvatarForComment(comment, lineNumber)) return false;

    // Always hide outdated comments from gutter display
    if (comment.isOutdated) return false;

    // Hide resolved comments when collapsed
    if (areResolvedCommentsCollapsed && comment.isResolved) return false;

    // Check side compatibility
    if (side) {
      return side === comment.startSide || side === comment.endSide;
    }

    return true;
  });
}

function findCommentsForLine(
  lineNumber: number | undefined,
  side: DiffSide | undefined,
  filePath: string,
  comments: ReviewComment[],
  areResolvedCommentsCollapsed?: boolean
): ReviewComment[] {
  return comments.filter((c) => {
    if (filePath !== c.path) return false;

    // Always hide outdated comments from line highlighting
    if (c.isOutdated) return false;

    // Hide resolved comments when collapsed
    if (areResolvedCommentsCollapsed && c.isResolved) return false;

    return isLineInCommentRange(lineNumber, side, c);
  });
}

/**
 * MesaDiff is a pure component that renders a diff view.
 * It accepts hunks and renders them in either unified or split view.
 */
export const MesaDiff = memo(function ({
  hunks,
  viewMode = 'unified',
  className,
  language,
  filename,
  owner,
  repo,
  sha,
}: MesaDiffProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  // --- Floating comment menu state ---
  const [menuCoords, setMenuCoords] = useState<{ top: number; left: number } | null>(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [lockedMenuPosition, setLockedMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [inputMode, setInputMode] = useState(false);

  // --- Code folding state ---
  const [gapStates, setGapStates] = useState<Map<string, GapState>>(new Map());
  const [gapErrors, setGapErrors] = useState<Map<string, string>>(new Map());

  // Raw file content hook (lazy - only fetches when explicitly called)
  const rawFileContent = useRawFileContent(owner, repo, sha, filename);

  // Use comment navigation from context
  const { navigateToAIChat } = useCommentNavigation();

  // Determine the language to use for syntax highlighting
  const detectedLanguage = useMemo(() => {
    if (language) return language;
    if (filename) return detectLanguage(filename);
    return 'typescript'; // Default to typescript
  }, [language, filename]);

  // Gap expansion handlers
  const handleGapExpand = useCallback(
    async (
      gapId: string,
      gap: { startOldLine: number; startNewLine: number; endOldLine: number; endNewLine: number; count: number }
    ) => {
      // Validate gap size
      const isValid = validateGapExpansion(gap.count);
      if (!isValid) {
        setGapErrors((prev) => new Map(prev).set(gapId, 'Gap is too large to expand'));
        setGapStates((prev) => new Map(prev).set(gapId, 'error'));
        return;
      }

      setGapStates((prev) => new Map(prev).set(gapId, 'loading'));
      setGapErrors((prev) => {
        const newMap = new Map(prev);
        newMap.delete(gapId);
        return newMap;
      });

      try {
        // Fetch raw file content using the hook
        const content = await rawFileContent.fetchContent();
        processGapExpansion(gapId, gap, content);
      } catch (error) {
        console.error('Error expanding gap:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to expand gap';
        setGapErrors((prev) => new Map(prev).set(gapId, errorMessage));
        setGapStates((prev) => new Map(prev).set(gapId, 'error'));
      }
    },
    [rawFileContent.fetchContent]
  );

  const processGapExpansion = useCallback(
    (
      gapId: string,
      gap: { startOldLine: number; startNewLine: number; endOldLine: number; endNewLine: number },
      content: string
    ) => {
      try {
        // Validate that we can extract lines successfully
        const lines = extractLinesFromRaw(content, gap.startNewLine, gap.endNewLine);

        // Validate that createContextLines works (but don't store the result)
        createContextLines(lines, gap.startOldLine, gap.startNewLine);

        // Set expanded state - actual contextLines will be created in processedHunks
        setGapStates((prev) => new Map(prev).set(gapId, 'expanded'));
      } catch (error) {
        console.error('Error processing gap expansion:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to process gap';
        setGapErrors((prev) => new Map(prev).set(gapId, errorMessage));
        setGapStates((prev) => new Map(prev).set(gapId, 'error'));
      }
    },
    []
  );

  const handleGapCollapse = useCallback((gapId: string) => {
    setGapStates((prev) => new Map(prev).set(gapId, 'collapsed'));
    setGapErrors((prev) => {
      const newMap = new Map(prev);
      newMap.delete(gapId);
      return newMap;
    });
  }, []);

  // Process hunks to add skipped line indicators between them
  const processedHunks = useMemo(() => {
    if (hunks.length <= 1) return hunks;

    const result: ProcessedHunk[] = [hunks[0]];

    // For each hunk (after the first), check if there's a gap with the previous one
    for (let i = 1; i < hunks.length; i++) {
      const prevHunk = hunks[i - 1];
      const currentHunk = hunks[i];

      // Calculate gap range using the utility function
      const gapRange = calculateGapRange(prevHunk, currentHunk);

      // If there's a gap, add a gap marker with full metadata
      if (gapRange.count > 0) {
        const gapId = createGapId(i, gapRange.startOldLine, gapRange.startNewLine);
        const gapState = gapStates.get(gapId) || 'collapsed';

        if (gapState === 'expanded') {
          // If expanded, we need to create context lines from raw content
          if (rawFileContent.content) {
            try {
              // raw file returned represents the new version
              const totalLines = rawFileContent.content.split('\n').length;
              const safeEndLine = Math.min(gapRange.endNewLine, totalLines);
              const lines = extractLinesFromRaw(rawFileContent.content, gapRange.startNewLine, safeEndLine);
              const contextLines = createContextLines(lines, gapRange.startOldLine, gapRange.startNewLine);

              result.push({
                type: 'expanded-gap',
                contextLines,
                gapId,
              });
            } catch (error) {
              // If there's an error creating context lines, fall back to collapsed gap
              result.push({
                type: 'gap',
                ...gapRange,
                gapId,
              });
            }
          } else {
            // Content not available, show as collapsed
            result.push({
              type: 'gap',
              ...gapRange,
              gapId,
            });
          }
        } else {
          // Collapsed or other states
          result.push({
            type: 'gap',
            ...gapRange,
            gapId,
          });
        }
      }

      // Add the current hunk
      result.push(currentHunk);
    }

    return result;
  }, [hunks, gapStates, rawFileContent.content]);

  // Enables independent selection of text on the old and new sides of a split diff.
  const enableColumnSelection = useCallback(({ target, button }: MouseEvent<HTMLDivElement>) => {
    // Only handle left clicks
    if (button !== 0) return;

    // Find the clicked code cell
    const closestCell = findClosest(target as HTMLElement, 'diff-code');
    if (!closestCell || !closestCell.parentElement) return;

    // Clear any existing selection
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }

    // Get the index of the clicked cell in its parent
    const index = [...closestCell.parentElement.children].indexOf(closestCell);
    // Only handle content columns (indices 2 and 5 after adding avatar + number columns)
    if (index !== 2 && index !== 5) return;

    // Get all diff lines
    const lines = rootRef.current?.querySelectorAll('.diff-line') ?? [];

    // Update selection styles for all lines
    for (const line of lines) {
      const cells = line.children;
      setUserSelectStyle(cells[2], index === 2);
      setUserSelectStyle(cells[5], index === 5);
    }
  }, []);

  const maybeEnableColumnSelection = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (viewMode === 'split') return enableColumnSelection(e);
    },
    [viewMode, enableColumnSelection]
  );

  // Hide menu on selection start
  const resetMenuPosition = useCallback(() => {
    setMenuVisible(false);
  }, []);

  // Show menu on selection end (if valid selection), using setTimeout to wait for browser update
  const maybeShowMenu = useCallback(() => {
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        setMenuVisible(false);
        return;
      }
      // ---------------------------------------------
      // 1. Gather text nodes & client rects in selection
      //    (restrict to the side the user started on when in split view)
      // ---------------------------------------------
      let nodes = getSelectedTextNodes(selection).filter(
        (n) => n.nodeType === Node.TEXT_NODE && (n.textContent?.trim().length ?? 0) > 0
      );

      if (viewMode === 'split') {
        // Figure out which column (LEFT = 0, RIGHT = 1) the selection started in.
        let columnIdx: 0 | 1 | null = null;
        const anchorNode = selection.anchorNode;
        let anchorCell: HTMLElement | null = null;
        if (anchorNode) {
          if (anchorNode.nodeType === Node.ELEMENT_NODE) {
            anchorCell = (anchorNode as HTMLElement).closest('.diff-code');
          } else if ((anchorNode as Node).parentElement) {
            anchorCell = (anchorNode as Node).parentElement!.closest('.diff-code');
          }
        }
        if (anchorCell) {
          const row = anchorCell.closest('.diff-line');
          if (row) {
            const codeCells = Array.from(row.querySelectorAll('.diff-code'));
            if (codeCells.length === 2) {
              const idx = codeCells.indexOf(anchorCell);
              if (idx === 0 || idx === 1) columnIdx = idx as 0 | 1;
            }
          }
        }

        if (columnIdx !== null) {
          nodes = nodes.filter((n) => {
            const cell = n.parentElement?.closest('.diff-code');
            if (!cell) return false;
            const row = cell.closest('.diff-line');
            if (!row) return false;
            const codeCells = Array.from(row.querySelectorAll('.diff-code'));
            if (codeCells.length !== 2) return false;
            return codeCells.indexOf(cell) === columnIdx;
          });
        }
      }

      if (nodes.length === 0) {
        setMenuVisible(false);
        return;
      }

      // ---------------------------------------------
      // 2. Identify the top-most, then left-most rect
      // ---------------------------------------------
      let bestRect: DOMRect | null = null;
      for (const node of nodes) {
        const rangeForNode = document.createRange();
        rangeForNode.selectNodeContents(node);
        const rect = rangeForNode.getBoundingClientRect();
        if (!bestRect) {
          bestRect = rect;
          continue;
        }
        if (rect.top < bestRect.top - 0.5 || (Math.abs(rect.top - bestRect.top) < 0.5 && rect.left < bestRect.left)) {
          bestRect = rect;
        }
      }

      if (!bestRect) {
        setMenuVisible(false);
        return;
      }

      // Calculate position relative to the container
      const containerRect = rootRef.current?.getBoundingClientRect();
      if (!containerRect) {
        setMenuVisible(false);
        return;
      }

      const leftCoord = bestRect.left + bestRect.width / 2 - containerRect.left;
      const topCoord = bestRect.top - 15 - containerRect.top;

      setMenuCoords({
        top: topCoord,
        left: leftCoord,
      });
      setMenuVisible(true);
    }, 0);
  }, [viewMode, rootRef]);

  // Memoize the table className
  const tableClassName = useMemo(() => cn('mesa-diff w-full max-w-full overflow-x-hidden', className), [className]);

  // Memoize event handlers
  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      maybeEnableColumnSelection(e);
      
      const target = e.target as HTMLElement;
      const isCodeContentClick = target.classList.contains('diff-code') || 
                                target.closest('.diff-code') !== null;
      
      if (!inputMode) {
        // Not in input mode - always reset menu position
        resetMenuPosition();
      } else {
        // In input mode - only keep menu open for clicks in code content
        if (!isCodeContentClick) {
          // Clicking outside code content - close menu and clear selection
          resetMenuPosition();
          const selection = window.getSelection();
          if (selection) {
            selection.removeAllRanges();
          }
        } else {
          // Clicking in code content while in input mode - keep menu open
          e.stopPropagation();
        }
      }
    },
    [maybeEnableColumnSelection, inputMode, resetMenuPosition]
  );

  const handleMouseUp = useCallback(() => maybeShowMenu(), [maybeShowMenu]);

  // Memoize the onCommentButtonClick handler to prevent recreating it on every render
  const handleCommentButtonClick = useCallback(() => {
    setLockedMenuPosition(menuCoords);
    setMenuVisible(false);
    setInputMode(true);
  }, [menuCoords]);

  // Memoize the onInputClose handler
  const handleInputClose = useCallback(() => {
    setLockedMenuPosition(null);
    setInputMode(false);
  }, []);

  // Memoize the onAskAIClick handler
  const handleAskAIClick = useCallback(
    (selectionData: { selectedText: string; selectionRange: CommentSelectionRange }) => {
      // Format the selected text with context
      const { selectedText, selectionRange } = selectionData;
      const { path, startLine, endLine, startSide, endSide } = selectionRange;

      // Create a structured message with attachments
      const attachment = {
        type: 'file_reference' as const,
        path,
        startLine,
        endLine,
        side: startSide || endSide || 'RIGHT',
        code: selectedText,
      };

      // Create a simple question without repeating the context
      const userQuestion = `What does this code do?`;

      // Navigate to AI chat with the attachment and question
      navigateToAIChat(userQuestion, [attachment]);

      // Hide the menu
      setMenuVisible(false);
      setLockedMenuPosition(null);
      setInputMode(false);
    },
    [navigateToAIChat]
  );

  // Re-clamp locked menu position when we enter/exit inputMode because the menu width changes.
  useEffect(() => {
    if (!rootRef.current) return;
    if (!lockedMenuPosition) return;

    const rootRect = rootRef.current.getBoundingClientRect();
    const PADDING = 8;
    const MENU_WIDTH_COLLAPSED = 56; // px (w-14)
    const MENU_WIDTH_INPUT = 340; // px (w-[340px])
    const halfWidth = (inputMode ? MENU_WIDTH_INPUT : MENU_WIDTH_COLLAPSED) / 2;

    const clampedLeft = Math.max(
      PADDING + halfWidth,
      Math.min(lockedMenuPosition.left, rootRect.width - halfWidth - PADDING)
    );

    if (clampedLeft !== lockedMenuPosition.left) {
      setLockedMenuPosition({ ...lockedMenuPosition, left: clampedLeft });
    }
  }, [inputMode, lockedMenuPosition]);

  // Close comment popover menu when clicking outside
  useEffect(() => {
    if (!menuVisible && !lockedMenuPosition) return;

    const handleDocumentClick = () => {
      setMenuVisible(false);
      setLockedMenuPosition(null);
      setInputMode(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [menuVisible, lockedMenuPosition]);

  // Context value for event handlers
  const diffEventContext = useMemo(() => ({
    onContentMouseUp: handleMouseUp
  }), [handleMouseUp]);

  return (
    <FileContext.Provider value={{ filePath: filename }}>
      <DiffEventContext.Provider value={diffEventContext}>
        <div ref={rootRef} className={cn(tableClassName, 'relative')} onMouseDown={handleMouseDown}>
        {/* Floating comment menu - render inside container */}
        {(menuVisible || lockedMenuPosition) && (
          <FloatingCommentMenu
            top={lockedMenuPosition?.top ?? menuCoords?.top ?? 0}
            left={lockedMenuPosition?.left ?? menuCoords?.left ?? 0}
            inputMode={inputMode}
            onCommentButtonClick={handleCommentButtonClick}
            onAskAIClick={handleAskAIClick}
            onInputClose={handleInputClose}
          />
        )}
        {/* Diff table */}
        <table className="font-mono text-xs bg-white table-fixed w-full leading-[19px] font-[375]">
          <colgroup>
            {/* Number + avatar + content (left) */}
            <col className="w-[4ch]" />
            <col className="w-6" />
            <col className={viewMode === 'split' ? 'w-1/2' : ''} />
            {viewMode === 'split' && (
              <>
                {/* Number + avatar + content (right) */}
                <col className="w-[4ch]" />
                <col className="w-6" />
                <col className="w-1/2" />
              </>
            )}
          </colgroup>
          <DiffSyntaxProvider language={detectedLanguage}>
            <tbody>
              {processedHunks.map((item, idx) => {
                if ('type' in item) {
                  if (item.type === 'gap') {
                    const gapState = gapStates.get(item.gapId) || 'collapsed';
                    const gapError = gapErrors.get(item.gapId);

                    const handleExpand = () => {
                      handleGapExpand(item.gapId, {
                        startOldLine: item.startOldLine,
                        startNewLine: item.startNewLine,
                        endOldLine: item.endOldLine,
                        endNewLine: item.endNewLine,
                        count: item.count,
                      });
                    };

                    const handleCollapse = () => {
                      handleGapCollapse(item.gapId);
                    };

                    return viewMode === 'unified' ? (
                      <SkippedLines
                        key={item.gapId}
                        count={item.count}
                        gapState={gapState}
                        onExpand={handleExpand}
                        onCollapse={handleCollapse}
                        error={gapError}
                      />
                    ) : (
                      <SplitSkippedLines
                        key={item.gapId}
                        count={item.count}
                        gapState={gapState}
                        onExpand={handleExpand}
                        onCollapse={handleCollapse}
                        error={gapError}
                      />
                    );
                  } else if (item.type === 'expanded-gap') {
                    const handleCollapse = () => {
                      handleGapCollapse(item.gapId);
                    };

                    // Create a pseudo-hunk for rendering
                    const contextHunk: DiffHunk = {
                      content: '',
                      oldStart: item.contextLines[0]?.oldLineNumber || 0,
                      oldLines: item.contextLines.length,
                      newStart: item.contextLines[0]?.newLineNumber || 0,
                      newLines: item.contextLines.length,
                      changes: item.contextLines,
                    };

                    return (
                      <React.Fragment key={item.gapId}>
                        <HunkPrefetch hunk={contextHunk} language={detectedLanguage} hunkKey={`expanded-${item.gapId}`}>
                          {viewMode === 'unified' ? (
                            <UnifiedDiff hunks={[contextHunk]} />
                          ) : (
                            <SplitDiff hunks={[contextHunk]} />
                          )}
                        </HunkPrefetch>
                      </React.Fragment>
                    );
                  }
                }

                const hunk = item as DiffHunk;
                const hunkKey = `h${idx}`;
                return (
                  <HunkPrefetch key={idx} hunk={hunk} language={detectedLanguage} hunkKey={hunkKey}>
                    {viewMode === 'unified' ? <UnifiedDiff hunks={[hunk]} /> : <SplitDiff hunks={[hunk]} />}
                  </HunkPrefetch>
                );
              })}
            </tbody>
          </DiffSyntaxProvider>
        </table>
        </div>
      </DiffEventContext.Provider>
    </FileContext.Provider>
  );
});

// --- Block renderers for UnifiedDiff ---
function renderNormalBlock(block: { change: DiffChange }, key: React.Key) {
  return <UnifiedDiffLine key={key} change={block.change} />;
}

function renderReplaceBlock(block: { deletes: DiffChange[]; inserts: DiffChange[] }, key: React.Key) {
  const isSingleLineReplace = block.deletes.length === 1 && block.inserts.length === 1;
  if (isSingleLineReplace) {
    const tokens = getTokenDiff(block.deletes[0].content, block.inserts[0].content);
    return [
      <UnifiedDiffLine key={`del-${key}`} change={block.deletes[0]} tokens={tokens} tokenType="delete" />,
      <UnifiedDiffLine key={`ins-${key}`} change={block.inserts[0]} tokens={tokens} tokenType="insert" />,
    ];
  }
  return [
    ...block.deletes.map((del, d) => <UnifiedDiffLine key={`del-${key}-${d}`} change={del} />),
    ...block.inserts.map((ins, insIdx) => <UnifiedDiffLine key={`ins-${key}-${insIdx}`} change={ins} />),
  ];
}

function renderDeleteBlock(block: { deletes: DiffChange[] }, key: React.Key) {
  return block.deletes.map((del, d) => <UnifiedDiffLine key={`del-${key}-${d}`} change={del} />);
}

function renderInsertBlock(block: { inserts: DiffChange[] }, key: React.Key) {
  return block.inserts.map((ins, insIdx) => <UnifiedDiffLine key={`ins-${key}-${insIdx}`} change={ins} />);
}

// Simple non-interactive skipped lines for internal gaps within hunks
const SimpleSkippedLines = memo(function ({ count }: { count: number }) {
  return (
    <tr className="diff-skipped-line">
      <td className={GUTTER_CLASSES}></td>
      <td className={AVATAR_CELL_CLASSES}></td>
      <td className="px-4 py-2 text-center font-sans text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-700 align-middle">
        <span>
          {count} {count === 1 ? 'line' : 'lines'} hidden
        </span>
      </td>
    </tr>
  );
});

// Simple non-interactive split skipped lines for internal gaps within hunks
const SimpleSplitSkippedLines = memo(function ({ count }: { count: number }) {
  return (
    <tr className="diff-skipped-line">
      <td className={GUTTER_CLASSES}></td>
      <td className={AVATAR_CELL_CLASSES}></td>
      <td
        colSpan={4}
        className="px-4 py-2 text-center font-sans text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-700 align-middle"
      >
        <span>
          {count} {count === 1 ? 'line' : 'lines'} hidden
        </span>
      </td>
    </tr>
  );
});

// Helper component to show skipped lines
const SkippedLines = memo(function ({
  count,
  gapState,
  onExpand,
  onCollapse,
  error,
}: {
  count: number;
  gapState: GapState;
  onExpand: () => void;
  onCollapse: () => void;
  error?: string;
}) {
  const isExpanded = gapState === 'expanded';
  const isLoading = gapState === 'loading';
  const hasError = gapState === 'error';

  const handleClick = () => {
    if (isLoading) return;
    onExpand();
  };

  return (
    <tr className="diff-skipped-line">
      {isExpanded ? (
        <td className={cn(GUTTER_CLASSES, 'cursor-pointer')}></td>
      ) : (
        <button
          onClick={handleClick}
          disabled={isLoading}
          className="w-full min-w-[52px] h-10 flex items-center justify-center bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors disabled:cursor-not-allowed text-muted-foreground"
          title="Expand"
        >
          {isLoading ? (
            <Loader2Icon className="w-5 h-5 cursor-pointer animate-spin text-muted-foreground" strokeWidth={1.0} />
          ) : (
            <SeparatorHorizontalIcon className="w-5 h-5 cursor-pointer text-muted-foreground" strokeWidth={1.0} />
          )}
        </button>
      )}
      <td className={AVATAR_CELL_CLASSES}></td>
      <td className="px-4 h-10 font-sans text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-700">
        <span>
          {`${count} hidden ${count === 1 ? 'line' : 'lines'}`}
          {hasError && error && <span className="text-red-500 ml-1">({error})</span>}
        </span>
      </td>
    </tr>
  );
});

function UnifiedDiff({ hunks }: { hunks: DiffHunk[] }) {
  const changes = hunks.flatMap((hunk) => hunk.changes);
  const rendered: React.ReactNode[] = [];

  // Handle line gaps in the diff
  let lastOldLineNumber: number | undefined;
  let lastNewLineNumber: number | undefined;

  for (const block of diffBlockIterator(changes)) {
    const key = rendered.length;

    // Check for gaps before rendering a block
    if (lastOldLineNumber !== undefined && lastNewLineNumber !== undefined) {
      let gapSize = 0;

      // For the current block, calculate the gap
      if (block.type === 'normal' && 'change' in block) {
        const oldGap = (block.change.oldLineNumber ?? 0) - (lastOldLineNumber + 1);
        const newGap = (block.change.newLineNumber ?? 0) - (lastNewLineNumber + 1);
        gapSize = Math.max(oldGap, newGap);
      } else if (block.type === 'delete' && 'deletes' in block && block.deletes.length > 0) {
        const oldGap = (block.deletes[0].oldLineNumber ?? 0) - (lastOldLineNumber + 1);
        gapSize = oldGap;
      } else if (block.type === 'insert' && 'inserts' in block && block.inserts.length > 0) {
        const newGap = (block.inserts[0].newLineNumber ?? 0) - (lastNewLineNumber + 1);
        gapSize = newGap;
      } else if (
        block.type === 'replace' &&
        'deletes' in block &&
        'inserts' in block &&
        (block.deletes.length > 0 || block.inserts.length > 0)
      ) {
        const oldGap = block.deletes.length > 0 ? (block.deletes[0].oldLineNumber ?? 0) - (lastOldLineNumber + 1) : 0;
        const newGap = block.inserts.length > 0 ? (block.inserts[0].newLineNumber ?? 0) - (lastNewLineNumber + 1) : 0;
        gapSize = Math.max(oldGap, newGap);
      }

      // If there's a gap, add a skipped lines indicator
      if (gapSize > 0) {
        rendered.push(<SimpleSkippedLines key={`gap-${key}`} count={gapSize} />);
      }
    }

    // Render the block
    switch (block.type) {
      case 'normal':
        rendered.push(renderNormalBlock(block, key));
        lastOldLineNumber = block.change.oldLineNumber;
        lastNewLineNumber = block.change.newLineNumber;
        break;
      case 'replace':
        rendered.push(...renderReplaceBlock(block, key));
        lastOldLineNumber =
          block.deletes.length > 0 ? block.deletes[block.deletes.length - 1].oldLineNumber : lastOldLineNumber;
        lastNewLineNumber =
          block.inserts.length > 0 ? block.inserts[block.inserts.length - 1].newLineNumber : lastNewLineNumber;
        break;
      case 'delete':
        rendered.push(...renderDeleteBlock(block, key));
        lastOldLineNumber =
          block.deletes.length > 0 ? block.deletes[block.deletes.length - 1].oldLineNumber : lastOldLineNumber;
        break;
      case 'insert':
        rendered.push(...renderInsertBlock(block, key));
        lastNewLineNumber =
          block.inserts.length > 0 ? block.inserts[block.inserts.length - 1].newLineNumber : lastNewLineNumber;
        break;
    }
  }

  return <>{rendered}</>;
}

// Show skipped lines in split view
const SplitSkippedLines = memo(function ({
  count,
  gapState,
  onExpand,
  onCollapse,
  error,
}: {
  count: number;
  gapState: GapState;
  onExpand: () => void;
  onCollapse: () => void;
  error?: string;
}) {
  const isExpanded = gapState === 'expanded';
  const isLoading = gapState === 'loading';
  const hasError = gapState === 'error';

  const handleClick = () => {
    if (isLoading) return;
    onExpand();
  };

  const textContent = (
    <span>
      {`${count} hidden ${count === 1 ? 'line' : 'lines'}`}
      {hasError && error && <span className="text-red-500 ml-1">({error})</span>}
    </span>
  );

  return (
    <tr className="diff-skipped-line">
      {isExpanded ? (
        <td className={cn(GUTTER_CLASSES, 'cursor-pointer')}></td>
      ) : (
        <td className={cn(GUTTER_CLASSES, 'p-0')}>
          <button
            onClick={handleClick}
            disabled={isLoading}
            className="w-full min-w-[52px] h-10 flex items-center justify-center bg-blue-50 dark:bg-blue-950 hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors disabled:cursor-not-allowed text-muted-foreground"
            title="Expand"
          >
            {isLoading ? (
              <Loader2Icon className="w-5 h-5 animate-spin cursor-pointer text-muted-foreground" strokeWidth={1.0} />
            ) : (
              <SeparatorHorizontalIcon className="w-5 h-5 cursor-pointer text-muted-foreground" strokeWidth={1.0} />
            )}
          </button>
        </td>
      )}
      <td className={AVATAR_CELL_CLASSES}></td>
      <td
        colSpan={4}
        className="px-4 py-3 font-sans text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-700"
      >
        {textContent}
      </td>
    </tr>
  );
});

const SplitDiff = memo(function ({ hunks }: { hunks: DiffHunk[] }) {
  const blockElements = useMemo(() => {
    const elements: JSX.Element[] = [];
    const changes = hunks.flatMap((hunk) => hunk.changes);
    let blockIdx = 0;

    // Track last line numbers to detect gaps
    let lastOldLineNumber: number | undefined;
    let lastNewLineNumber: number | undefined;

    for (const block of diffBlockIterator(changes)) {
      const key = blockIdx++;

      // Check for gaps before rendering a block
      if (lastOldLineNumber !== undefined && lastNewLineNumber !== undefined) {
        let gapSize = 0;

        // For the current block, calculate the gap
        if (block.type === 'normal' && 'change' in block) {
          const oldGap = (block.change.oldLineNumber ?? 0) - (lastOldLineNumber + 1);
          const newGap = (block.change.newLineNumber ?? 0) - (lastNewLineNumber + 1);
          gapSize = Math.max(oldGap, newGap);
        } else if (block.type === 'delete' && 'deletes' in block && block.deletes.length > 0) {
          const oldGap = (block.deletes[0].oldLineNumber ?? 0) - (lastOldLineNumber + 1);
          gapSize = oldGap;
        } else if (block.type === 'insert' && 'inserts' in block && block.inserts.length > 0) {
          const newGap = (block.inserts[0].newLineNumber ?? 0) - (lastNewLineNumber + 1);
          gapSize = newGap;
        } else if (
          block.type === 'replace' &&
          'deletes' in block &&
          'inserts' in block &&
          (block.deletes.length > 0 || block.inserts.length > 0)
        ) {
          const oldGap = block.deletes.length > 0 ? (block.deletes[0].oldLineNumber ?? 0) - (lastOldLineNumber + 1) : 0;
          const newGap = block.inserts.length > 0 ? (block.inserts[0].newLineNumber ?? 0) - (lastNewLineNumber + 1) : 0;
          gapSize = Math.max(oldGap, newGap);
        }

        // If there's a gap, add a skipped lines indicator
        if (gapSize > 0) {
          elements.push(<SimpleSplitSkippedLines key={`gap-${key}`} count={gapSize} />);
        }
      }

      // Render the block and update last line numbers
      switch (block.type) {
        case 'normal':
          elements.push(<SplitDiffLine key={key} oldChange={block.change} newChange={block.change} />);
          lastOldLineNumber = block.change.oldLineNumber;
          lastNewLineNumber = block.change.newLineNumber;
          break;
        case 'replace':
          elements.push(<MemoizedSplitReplaceBlock key={`replace-${key}`} block={block} blockKey={key} />);
          lastOldLineNumber =
            block.deletes.length > 0 ? block.deletes[block.deletes.length - 1].oldLineNumber : lastOldLineNumber;
          lastNewLineNumber =
            block.inserts.length > 0 ? block.inserts[block.inserts.length - 1].newLineNumber : lastNewLineNumber;
          break;
        case 'delete':
          elements.push(<MemoizedSplitDeleteBlock key={`delete-${key}`} block={block} blockKey={key} />);
          lastOldLineNumber =
            block.deletes.length > 0 ? block.deletes[block.deletes.length - 1].oldLineNumber : lastOldLineNumber;
          break;
        case 'insert':
          elements.push(<MemoizedSplitInsertBlock key={`insert-${key}`} block={block} blockKey={key} />);
          lastNewLineNumber =
            block.inserts.length > 0 ? block.inserts[block.inserts.length - 1].newLineNumber : lastNewLineNumber;
          break;
      }
    }

    return elements;
  }, [hunks]);

  return <>{blockElements}</>;
});

interface DiffLineProps {
  change: DiffChange;
  tokens?: ReturnType<typeof getTokenDiff>;
  tokenType?: 'insert' | 'delete';
}

function getUnifiedGutterNumber(change: DiffChange): number | string | undefined {
  switch (change.type) {
    case 'normal':
      if (change.oldLineNumber && change.newLineNumber && change.oldLineNumber === change.newLineNumber) {
        return change.oldLineNumber;
      }
      if (change.oldLineNumber && change.newLineNumber) {
        // return `${change.oldLineNumber}/${change.newLineNumber}`;
        return change.newLineNumber;
      }
      return change.oldLineNumber ?? change.newLineNumber;
    case 'insert':
      return change.newLineNumber;
    case 'delete':
      return change.oldLineNumber;
    default:
      return '';
  }
}

const UnifiedDiffLine = memo(function ({ change, tokens, tokenType }: DiffLineProps) {
  const comments = useReviewComments();
  const { filePath } = useFileContext();
  const { areResolvedCommentsCollapsed } = useSidebar();
  const { onContentMouseUp } = useDiffEvents();
  const gutterNumber = getUnifiedGutterNumber(change);
  const content = change ? highlight(change.content, tokens, tokenType) : null;

  // Determine side & line number for comment matching
  let side: DiffSide | undefined = undefined;
  let gutterComments: ReviewComment[] = []; // For avatar/count display
  let highlightComments: ReviewComment[] = []; // For line highlighting
  let firstComment: ReviewComment | undefined = undefined;
  let lineNumber: number | undefined = undefined;

  // Try to get the file path from the first comment or from elsewhere
  if (change.type === 'delete') {
    side = 'LEFT';
    lineNumber = change.oldLineNumber;
    gutterComments = change ? findCommentsForGutterDisplay(change.oldLineNumber, 'LEFT', filePath, comments, areResolvedCommentsCollapsed) : [];
    highlightComments = change ? findCommentsForLine(change.oldLineNumber, 'LEFT', filePath, comments, areResolvedCommentsCollapsed) : [];
    firstComment = gutterComments.length > 0 ? gutterComments[0] : undefined;
  } else if (change.type === 'insert') {
    side = 'RIGHT';
    lineNumber = change.newLineNumber;
    gutterComments = change ? findCommentsForGutterDisplay(change.newLineNumber, 'RIGHT', filePath, comments, areResolvedCommentsCollapsed) : [];
    highlightComments = change ? findCommentsForLine(change.newLineNumber, 'RIGHT', filePath, comments, areResolvedCommentsCollapsed) : [];
    firstComment = gutterComments.length > 0 ? gutterComments[0] : undefined;
  } else {
    // Normal line present on both sides – leave side undefined to match comments regardless of side
    const leftGutterComments = findCommentsForGutterDisplay(change.oldLineNumber, 'LEFT', filePath, comments, areResolvedCommentsCollapsed);
    const rightGutterComments = findCommentsForGutterDisplay(change.newLineNumber, 'RIGHT', filePath, comments, areResolvedCommentsCollapsed);
    const leftHighlightComments = findCommentsForLine(change.oldLineNumber, 'LEFT', filePath, comments, areResolvedCommentsCollapsed);
    const rightHighlightComments = findCommentsForLine(change.newLineNumber, 'RIGHT', filePath, comments, areResolvedCommentsCollapsed);

    if (leftGutterComments.length > 0) {
      gutterComments = leftGutterComments;
      highlightComments = leftHighlightComments;
      lineNumber = change.oldLineNumber;
      side = 'LEFT';
    } else {
      gutterComments = rightGutterComments;
      highlightComments = rightHighlightComments;
      lineNumber = change.newLineNumber;
      side = 'RIGHT';
    }
    firstComment = gutterComments.length > 0 ? gutterComments[0] : undefined;
  }

  const showAvatar = !!firstComment && !!lineNumber && shouldShowAvatarForComment(firstComment, lineNumber);

  const cellClassName = useMemo(
    () =>
      cn(
        {
          [BASE_CONTENT_CLASSES]: change.type === 'normal',
          [INSERT_CONTENT_CLASSES]: change.type === 'insert',
          [DELETE_CONTENT_CLASSES]: change.type === 'delete',
        },
        highlightComments.length > 0 && 'mesa-comment-highlight' // highlight based on all comments that touch this line
      ),
    [change.type, highlightComments.length]
  );

  // Data attributes for line jumping
  const dataAttributes: Record<string, string | number | undefined> = {};
  if (change.type === 'delete' || change.type === 'normal') {
    dataAttributes['data-old-line-number'] = change.oldLineNumber;
  }
  if (change.type === 'insert' || change.type === 'normal') {
    dataAttributes['data-new-line-number'] = change.newLineNumber;
  }

  return (
    <tr className="diff-line" {...dataAttributes}>
      <NumberCell number={gutterNumber} />
      <AvatarCell
        comment={firstComment}
        showAvatar={showAvatar}
        commentCount={gutterComments.length} // count only comments that should show avatar on this line
        lineNumber={lineNumber ?? 0} // TODO: handle file-level comments (don't have line number)
        side={side}
      />
      <td className={cellClassName} onMouseUp={onContentMouseUp}>{content}</td>
    </tr>
  );
});

const SplitDiffLine = memo(
  function ({
    oldChange,
    newChange,
    tokens,
  }: {
    oldChange: DiffChange | null;
    newChange: DiffChange | null;
    tokens?: ReturnType<typeof getTokenDiff>;
  }) {
    // Data attributes for line jumping
    const dataAttributes: Record<string, string | number | undefined> = {};
    if (oldChange && oldChange.oldLineNumber !== undefined) {
      dataAttributes['data-old-line-number'] = oldChange.oldLineNumber;
    }
    if (newChange && newChange.newLineNumber !== undefined) {
      dataAttributes['data-new-line-number'] = newChange.newLineNumber;
    }

    return (
      <tr className="diff-line" {...dataAttributes}>
        <OldCell change={oldChange} tokens={tokens} />
        <NewCell change={newChange} tokens={tokens} />
      </tr>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for memoization
    // Only re-render if the line numbers or content has changed
    const oldLineNumEqual = prevProps.oldChange?.oldLineNumber === nextProps.oldChange?.oldLineNumber;
    const newLineNumEqual = prevProps.newChange?.newLineNumber === nextProps.newChange?.newLineNumber;
    const oldContentEqual = prevProps.oldChange?.content === nextProps.oldChange?.content;
    const newContentEqual = prevProps.newChange?.content === nextProps.newChange?.content;

    return oldLineNumEqual && newLineNumEqual && oldContentEqual && newContentEqual;
  }
);

const OldCell = memo(function ({
  change,
  tokens,
}: {
  change: DiffChange | null;
  tokens?: ReturnType<typeof getTokenDiff>;
}) {
  const comments = useReviewComments();
  const { filePath } = useFileContext();
  const { areResolvedCommentsCollapsed } = useSidebar();
  const { onContentMouseUp } = useDiffEvents();
  const content = change ? highlight(change.content, tokens, 'delete') : null;

  const gutterComments = change ? findCommentsForGutterDisplay(change.oldLineNumber, 'LEFT', filePath, comments, areResolvedCommentsCollapsed) : [];
  const highlightComments = change ? findCommentsForLine(change.oldLineNumber, 'LEFT', filePath, comments, areResolvedCommentsCollapsed) : [];
  const firstComment = gutterComments.length > 0 ? gutterComments[0] : undefined;

  const showAvatar =
    !!firstComment &&
    !!change &&
    !!change.oldLineNumber &&
    shouldShowAvatarForComment(firstComment, change.oldLineNumber);

  const cellClassName = useMemo(
    () =>
      cn(
        change ? (change.type === 'delete' ? DELETE_CONTENT_CLASSES : BASE_CONTENT_CLASSES) : OMIT_CONTENT_CLASSES,
        highlightComments.length > 0 && 'mesa-comment-highlight'
      ),
    [change?.type, highlightComments.length]
  );

  return (
    <>
      <NumberCell number={change?.oldLineNumber} />
      <AvatarCell
        comment={firstComment}
        showAvatar={showAvatar}
        commentCount={gutterComments.length}
        lineNumber={change?.oldLineNumber ?? 0} // TODO: handle file-level comments (don't have line number)
        side="LEFT"
      />
      <td className={cellClassName} onMouseUp={onContentMouseUp}>{content}</td>
    </>
  );
});

const NewCell = memo(function ({
  change,
  tokens,
}: {
  change: DiffChange | null;
  tokens?: ReturnType<typeof getTokenDiff>;
}) {
  const comments = useReviewComments();
  const { filePath } = useFileContext();
  const { areResolvedCommentsCollapsed } = useSidebar();
  const { onContentMouseUp } = useDiffEvents();
  const content = change ? highlight(change.content, tokens, 'insert') : null;

  const gutterComments = change ? findCommentsForGutterDisplay(change.newLineNumber, 'RIGHT', filePath, comments, areResolvedCommentsCollapsed) : [];
  const highlightComments = change ? findCommentsForLine(change.newLineNumber, 'RIGHT', filePath, comments, areResolvedCommentsCollapsed) : [];
  const firstComment = gutterComments.length > 0 ? gutterComments[0] : undefined;
  const showAvatar =
    !!firstComment &&
    !!change &&
    !!change.newLineNumber &&
    shouldShowAvatarForComment(firstComment, change.newLineNumber);

  const cellClassName = useMemo(
    () =>
      cn(
        change ? (change.type === 'insert' ? INSERT_CONTENT_CLASSES : BASE_CONTENT_CLASSES) : OMIT_CONTENT_CLASSES,
        highlightComments.length > 0 && 'mesa-comment-highlight'
      ),
    [change?.type, highlightComments.length]
  );

  return (
    <>
      <NumberCell number={change?.newLineNumber} />
      <AvatarCell
        comment={firstComment}
        showAvatar={showAvatar}
        commentCount={gutterComments.length}
        lineNumber={change?.newLineNumber ?? 0} // TODO: handle file-level comments (don't have line number)
        side="RIGHT"
      />
      <td className={cellClassName} onMouseUp={onContentMouseUp}>{content}</td>
    </>
  );
});

// Memoize block renderers to avoid recreating components on every render
const MemoizedSplitReplaceBlock = memo(function ({
  block,
  blockKey,
}: {
  block: { deletes: DiffChange[]; inserts: DiffChange[] };
  blockKey: React.Key;
}) {
  const maxLen = Math.max(block.deletes.length, block.inserts.length);
  const rows: JSX.Element[] = [];
  for (let k = 0; k < maxLen; k++) {
    const oldChange = block.deletes[k] ?? null;
    const newChange = block.inserts[k] ?? null;
    const tokens = oldChange && newChange ? getTokenDiff(oldChange.content, newChange.content) : undefined;
    rows.push(
      <SplitDiffLine key={`${blockKey}-replace-${k}`} oldChange={oldChange} newChange={newChange} tokens={tokens} />
    );
  }
  return <>{rows}</>;
});

const MemoizedSplitDeleteBlock = memo(function ({
  block,
  blockKey,
}: {
  block: { deletes: DiffChange[] };
  blockKey: React.Key;
}) {
  return (
    <>
      {block.deletes.map((del, d) => (
        <SplitDiffLine key={`del-${blockKey}-${d}`} oldChange={del} newChange={null} />
      ))}
    </>
  );
});

const MemoizedSplitInsertBlock = memo(function ({
  block,
  blockKey,
}: {
  block: { inserts: DiffChange[] };
  blockKey: React.Key;
}) {
  return (
    <>
      {block.inserts.map((ins, insIdx) => (
        <SplitDiffLine key={`ins-${blockKey}-${insIdx}`} oldChange={null} newChange={ins} />
      ))}
    </>
  );
});
